/**
 * Nav Favorites — per-device pinned sidebar shortcuts.
 *
 * Stores an ordered list of favorited nav-item hrefs in localStorage. The
 * sidebar resolves each href back to its NavItem (label/icon) from the live
 * zones, so pinned entries always reflect the current nav config.
 *
 * Per-device for now (localStorage); DB-backed cross-device sync is a follow-up.
 *
 * @since 1.0.0
 */

const STORAGE_KEY = 'karr-fav-navitems';

/** Read the ordered list of favorited hrefs. Safe on SSR / blocked storage. */
export function getFavoriteHrefs(): string[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
    } catch {
        return [];
    }
}

/** Persist the ordered list of favorited hrefs. */
function setFavoriteHrefs(hrefs: string[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(hrefs));
    } catch {
        /* storage unavailable — favorites simply won't persist */
    }
}

/** Toggle a href's favorite state; returns the new ordered list. */
export function toggleFavoriteHref(href: string): string[] {
    const current = getFavoriteHrefs();
    const next = current.includes(href)
        ? current.filter(h => h !== href)
        : [...current, href];
    setFavoriteHrefs(next);
    return next;
}

export function isFavoriteHref(favorites: string[], href: string): boolean {
    return favorites.includes(href);
}
